<script setup>
import { reactive, ref, watch } from 'vue'
import ModalDialog from './ModalDialog.vue'
import { options, priceUnitLabels } from '../api/mappers'

const props = defineProps({
  open: Boolean,
  resource: { type: Object, default: null },
  saving: Boolean,
})
const emit = defineEmits(['close', 'save'])

const form = reactive({
  name: '',
  resourceType: 'house',
  capacity: 2,
  description: '',
  weekdayPrice: '',
  weekendPrice: '',
  priceUnit: 'per_night',
  isActive: true,
  color: '#b85f42',
})
const validationError = ref('')

watch(() => props.open, (open) => {
  if (!open) return
  validationError.value = ''
  Object.assign(form, {
    name: props.resource?.name ?? '',
    resourceType: props.resource?.resourceType ?? 'house',
    capacity: props.resource?.capacity ?? 2,
    description: props.resource?.description ?? '',
    weekdayPrice: props.resource?.weekdayPrice ?? '',
    weekendPrice: props.resource?.weekendPrice ?? '',
    priceUnit: props.resource?.priceUnit ?? 'per_night',
    isActive: props.resource?.isActive ?? true,
    color: props.resource?.color || '#b85f42',
  })
})

function submit() {
  validationError.value = ''
  if (!form.name.trim()) {
    validationError.value = 'Введите название объекта.'
    return
  }
  if (Number(form.capacity) < 1) {
    validationError.value = 'Вместимость должна быть не меньше одного гостя.'
    return
  }
  for (const field of ['weekdayPrice', 'weekendPrice']) {
    if (form[field] !== '' && (Number.isNaN(Number(form[field])) || Number(form[field]) < 0)) {
      validationError.value = 'Цена должна быть неотрицательным числом.'
      return
    }
  }
  emit('save', { ...form })
}
</script>

<template>
  <ModalDialog
    :open="open"
    :title="resource ? 'Редактировать объект' : 'Новый объект'"
    :eyebrow="resource ? 'Настройки объекта' : 'Инфраструктура базы'"
    @close="!saving && emit('close')"
  >
    <form class="dialog-form" @submit.prevent="submit">
      <div class="form-grid form-grid--2">
        <label class="field field--span-2">
          <span>Название <b aria-hidden="true">*</b></span>
          <input v-model="form.name" type="text" maxlength="160" required autofocus placeholder="Дом «Сосновый»" />
        </label>
        <label class="field">
          <span>Тип объекта <b aria-hidden="true">*</b></span>
          <select v-model="form.resourceType" required>
            <option v-for="type in options.resourceTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
          </select>
        </label>
        <label class="field">
          <span>Вместимость <b aria-hidden="true">*</b></span>
          <input v-model.number="form.capacity" type="number" min="1" max="999" required />
        </label>
        <label class="field field--span-2">
          <span>Описание</span>
          <textarea v-model="form.description" rows="4" maxlength="1000" placeholder="Короткая заметка для администратора" />
        </label>
        <fieldset class="price-fields field--span-2">
          <legend>Тариф</legend>
          <label class="field">
            <span>Будни, ₽</span>
            <input v-model="form.weekdayPrice" type="number" min="0" max="9999999999.99" step="0.01" inputmode="decimal" placeholder="Не указан" />
          </label>
          <label class="field">
            <span>Выходные, ₽</span>
            <input v-model="form.weekendPrice" type="number" min="0" max="9999999999.99" step="0.01" inputmode="decimal" placeholder="Не указан" />
          </label>
          <label class="field">
            <span>Единица тарифа</span>
            <select v-model="form.priceUnit">
              <option v-for="(label, value) in priceUnitLabels" :key="value" :value="value">{{ label }}</option>
            </select>
          </label>
        </fieldset>
        <label class="field field--color">
          <span>Цвет в календаре</span>
          <span class="color-field">
            <input v-model="form.color" type="color" aria-label="Цвет объекта" />
            <code>{{ form.color }}</code>
          </span>
        </label>
        <label class="switch-field">
          <input v-model="form.isActive" type="checkbox" />
          <span class="switch" aria-hidden="true" />
          <span>
            <strong>Объект активен</strong>
            <small>Можно создавать новые брони</small>
          </span>
        </label>
      </div>

      <p v-if="validationError" class="form-error" role="alert">{{ validationError }}</p>

      <footer class="modal-actions">
        <button type="button" class="button button--ghost" :disabled="saving" @click="emit('close')">Отмена</button>
        <button type="submit" class="button button--primary" :disabled="saving">
          <span v-if="saving" class="button-spinner" aria-hidden="true" />
          {{ saving ? 'Сохраняем…' : resource ? 'Сохранить изменения' : 'Добавить объект' }}
        </button>
      </footer>
    </form>
  </ModalDialog>
</template>
